import React, {useMemo, useRef, useState} from 'react';
import {Form, Schema, Input, SelectPicker, Button, Loader, Stack} from 'rsuite';
import type {FormInstance} from 'rsuite';
import {useCartQuery} from '@/api/useCartQuery';
import {useUpdateCart} from '@/api/useUpdateCart';
import type {Address} from '@/types';
import {useGetCountries} from '@/api/useGetCountries';
import {useGetAddressById} from '@/api/useGetAddressById';
import {useSaveAddress} from '@/api/useSaveAddress';
import {useQueryClient} from '@tanstack/react-query';
import {cn} from '@bem-react/classname';
import styles from './CheckoutAddress.module.scss';
import {ShippingMethods} from "@/components/checkout/shipping-methods";
import CheckoutEmail from "@/components/checkout/email/CheckoutEmail";
import {useSessionInfoQuery} from '@/api/useSessionInfoQuery';

type Props = {
  showTitle?: boolean;
};

// Fallback country (Craft id) used by the Twig template as default
const DEFAULT_COUNTRY_ID = 167;

const {StringType, NumberType} = Schema.Types;

export default function CheckoutAddress({showTitle = true}: Props) {
  const cName = cn(styles.cCheckoutAddress);
  const formRef = useRef<FormInstance>(null);
  const {data: cart, isLoading: cartLoading} = useCartQuery();
  const updateCart = useUpdateCart();

  const {data: countries, isLoading: countriesLoading} = useGetCountries();

  // Geo info now comes from session info (geo.countryIso)
  const {data: sessionInfo} = useSessionInfoQuery();
  const geoCountryId = useMemo(() => {
    const iso = sessionInfo?.geo?.countryIso?.toUpperCase?.();
    if (!iso || !countries || !Array.isArray(countries)) return undefined;
    const match = countries.find((c) => c.iso?.toUpperCase?.() === iso);
    return match?.id;
  }, [sessionInfo?.geo?.countryIso, countries]);

  // Determine the shippingAddressId from cart and fetch the address via API.
  const shippingAddressId: string | number | null = useMemo(() => {
    const c: any = cart as any;
    if (!c) return null;
    return c.shippingAddressId ?? c.selectedShippingAddressId ?? c.shippingAddress?.id ?? null;
  }, [cart]);

  const {data: fetchedAddress, isLoading: addressLoading} = useGetAddressById(shippingAddressId);

  // Final address used to populate the form
  const address: Address | null = fetchedAddress ?? cart?.shippingAddress ?? cart?.estimatedShippingAddress ?? null;

  const saveAddressMutation = useSaveAddress();
  const qc = useQueryClient();
  const lastSavedRef = useRef<Record<string, any>>({});

  // Track last saved values to avoid redundant updates
  React.useEffect(() => {
    const a = address ?? {};
    lastSavedRef.current = {
      firstName: a?.firstName ?? '',
      lastName: a?.lastName ?? '',
      address1: a?.address1 ?? '',
      zipCode: a?.zipCode ?? '',
      city: a?.city ?? '',
      countryId: a?.countryId ?? geoCountryId ?? DEFAULT_COUNTRY_ID,
    };
  }, [address?.id, address?.firstName, address?.lastName, address?.address1, address?.zipCode, address?.city, address?.countryId, geoCountryId]);

  type FormValue = {
    addressId?: number;
    firstName?: string;
    lastName?: string;
    address1?: string;
    zipCode?: string;
    city?: string;
    countryId?: number | null;
  };

  const [formValue, setFormValue] = useState<FormValue>(() => ({
    addressId: address?.id,
    firstName: address?.firstName ?? '',
    lastName: address?.lastName ?? '',
    address1: address?.address1 ?? '',
    zipCode: address?.zipCode ?? '',
    city: address?.city ?? '',
    countryId: address?.countryId ?? geoCountryId ?? DEFAULT_COUNTRY_ID,
  }));

  // Re-init form when cart or fetched address changes (e.g., after mutation)
  React.useEffect(() => {
    const a = address;
    setFormValue((prev) => {
      const computedCountry = a?.countryId ?? geoCountryId ?? DEFAULT_COUNTRY_ID;
      // If user already selected a country and it's different from previous default, keep it
      const keepExisting = prev?.addressId === a?.id && prev?.countryId && a?.countryId == null;
      return {
        addressId: a?.id,
        firstName: a?.firstName ?? '',
        lastName: a?.lastName ?? '',
        address1: a?.address1 ?? '',
        zipCode: a?.zipCode ?? '',
        city: a?.city ?? '',
        countryId: keepExisting ? prev.countryId : computedCountry,
      };
    });
  }, [address?.id, address?.firstName, address?.lastName, address?.address1, address?.zipCode, address?.city, address?.countryId, geoCountryId]);

  const model = useMemo(() => {
    return Schema.Model({
      firstName: StringType().isRequired('First name is required'),
      lastName: StringType().isRequired('Last name is required'),
      address1: StringType().isRequired('Address is required'),
      zipCode: StringType().isRequired('Zip code is required'),
      city: StringType().isRequired('City is required'),
      countryId: NumberType().isRequired('Country is required'),
    });
  }, []);


  const handleFieldChange = async (
    field: 'firstName' | 'lastName' | 'address1' | 'zipCode' | 'city' | 'countryId',
    nextValue?: any
  ) => {
    // Merge the potential nextValue into a local snapshot to avoid race conditions
    const merged = nextValue !== undefined ? {...formValue, [field]: nextValue} : formValue;

    const newVal = (merged as any)[field];
    const prevRef = lastSavedRef.current || {};
    const prevVal = (prevRef as any)[field];

    if (newVal === prevVal) return;

    // Validate entire form before sending any request. If invalid, just keep local state.
    if (formRef.current) {
      const valid = await formRef.current.check();
      if (!valid) {
        return;
      }
    }

    // If we don't yet have a shipping address, create it lazily once the form is valid.
    const idToUse = (merged.addressId ?? shippingAddressId) as number | string | null;

    // Build full payload from merged form snapshot
    const fullPayload: Record<string, unknown> = {
      firstName: merged.firstName ?? '',
      lastName: merged.lastName ?? '',
      address1: merged.address1 ?? '',
      zipCode: merged.zipCode ?? '',
      city: merged.city ?? '',
      countryId: merged.countryId ?? geoCountryId ?? DEFAULT_COUNTRY_ID,
    };

    saveAddressMutation.mutate(
      {id: idToUse ? Number(idToUse) : undefined, payload: fullPayload},
      {
        onSuccess: (addr: any) => {
          const returnedId = Number((addr as any)?.id);
          if (returnedId && !idToUse) {
            setFormValue((fv) => ({...fv, addressId: returnedId}));
          }
          (lastSavedRef.current as any)[field] = newVal;
        },
      } as any
    );
  };

  const onSubmit = async () => {
    if (!formRef.current) return;
    const valid = await formRef.current.check();
    if (!valid) return;

    const payload: Record<string, unknown> = {
      billingAddressSameAsShipping: 1,
      'shippingAddress[id]': formValue.addressId ?? '',
      'shippingAddress[firstName]': formValue.firstName ?? '',
      'shippingAddress[lastName]': formValue.lastName ?? '',
      'shippingAddress[address1]': formValue.address1 ?? '',
      'shippingAddress[zipCode]': formValue.zipCode ?? '',
      'shippingAddress[city]': formValue.city ?? '',
      'shippingAddress[countryId]': formValue.countryId ?? '',
    };

    await updateCart.mutateAsync(payload);
  };

  if (cartLoading || countriesLoading || addressLoading) {
    return (
      <div style={{padding: 12}}>
        <Loader/>
      </div>
    );
  }


  const isAddressValid = Boolean(
    (formValue.firstName ?? '').trim() &&
    (formValue.lastName ?? '').trim() &&
    (formValue.address1 ?? '').trim() &&
    (formValue.zipCode ?? '').trim() &&
    (formValue.city ?? '').trim() &&
    formValue.countryId
  );

  const isToPaymentDisabled = !isAddressValid || !cart?.email || !cart?.shippingMethodHandle;

  const goToPayment = () => {
    if (isToPaymentDisabled) return;
    window.location.assign('/shop/checkout?step=payment');
  };

  return (
    <div className={cName()}>
      <h2 className={cName('Title')}>Shipping address</h2>

      <div className={'pb-3'}>
        <CheckoutEmail/>
      </div>

      <Form
        ref={formRef}
        model={model}
        formValue={formValue}
        onChange={(v) => setFormValue(v as FormValue)}
        fluid
      >

        <Form.Group controlId="firstName">
          <Form.ControlLabel>First name</Form.ControlLabel>
          <Form.Control name="firstName" accepter={Input} onBlur={() => handleFieldChange('firstName')}/>
        </Form.Group>

        <Form.Group controlId="lastName">
          <Form.ControlLabel>Last name</Form.ControlLabel>
          <Form.Control name="lastName" accepter={Input} onBlur={() => handleFieldChange('lastName')}/>
        </Form.Group>

        <Form.Group controlId="address1">
          <Form.ControlLabel>Address</Form.ControlLabel>
          <Form.Control name="address1" accepter={Input} onBlur={() => handleFieldChange('address1')}/>
        </Form.Group>

        <div className={cName('ZipCity')}>
          <Form.Group controlId="zipCode" className={cName('Zip')}>
            <Form.ControlLabel>Zip code</Form.ControlLabel>
            <Form.Control name="zipCode" accepter={Input} onBlur={() => handleFieldChange('zipCode')}/>
          </Form.Group>
          <Form.Group controlId="city" className={cName('City')}>
            <Form.ControlLabel>City</Form.ControlLabel>
            <Form.Control name="city" accepter={Input} onBlur={() => handleFieldChange('city')}/>
          </Form.Group>
        </div>

        <Form.Group controlId="countryId">
          <Form.ControlLabel>Country</Form.ControlLabel>
          <Form.Control
            name="countryId"
            accepter={SelectPicker}
            data={(countries ?? []).map((c) => ({label: c.name, value: c.id}))}
            searchable
            cleanable={false}
            style={{width: '100%'}}
            onChange={(val: any) => handleFieldChange('countryId', val)}
          />
        </Form.Group>

        {/* Hidden field to mirror Twig semantics */}
        <input type="hidden" name="billingAddressSameAsShipping" value="1"/>

      </Form>


      {cart?.lineItems?.length !== 0 && (
        <>
          <ShippingMethods/>

          <div style={{marginTop: 16}}>
            <Button className={'w-full'} appearance="primary" size="lg" onClick={goToPayment}
                    disabled={isToPaymentDisabled}>
              To payment
            </Button>
          </div>
        </>
      )}

    </div>
  );
}
